import { createAsyncThunk } from "@reduxjs/toolkit";

import { RootState } from "../../../app/store";
import IncidentDetectionLapseReviewAPI, {
  IncidentDetectionLapseReview,
  GetIncidentDetectionLapseReviewResponse,
} from "../../../api/incidentDetectionLapseReview";

export const fetchIncidentDetectionLapseReview = createAsyncThunk<
  GetIncidentDetectionLapseReviewResponse,
  string,
  { state: RootState; rejectValue: string }
>(
  "incidentDetectionLapseReview/fetchIncidentDetectionLapseReview",
  async (caseId, { rejectWithValue }) => {
    try {
      return await IncidentDetectionLapseReviewAPI.get(caseId);
    } catch {
      return rejectWithValue( "Failed to fetch incident detection lapse review");
    }
  },
  {
    condition(caseId, { getState }) {
      const { caseId: id, status } = getState().incidentDetectionLapseReview;
      return status !== "loading" && (id !== caseId || status === "failed");
    },
  },
);

export const upsertIncidentDetectionLapseReview = createAsyncThunk<
  IncidentDetectionLapseReview,
  { caseId: string; data: Partial<IncidentDetectionLapseReview> },
  { state: RootState; rejectValue: string }
>(
  "incidentDetectionLapseReview/upsertIncidentDetectionLapseReview",
  async ({ caseId, data }, { getState, rejectWithValue }) => {
    const { status, caseId: currentCaseId, incidentDetectionLapseReview } =
      getState().incidentDetectionLapseReview;

    if (status !== "succeeded" || currentCaseId !== caseId) {
      return rejectWithValue("Cannot save: review data is not loaded for this case. Please refresh the page.");
    }

    const fullPayload: IncidentDetectionLapseReview = {
      controlling_office_could_detect_incident: null,
      controlling_office_detection_remarks: null,
      irregularities_reported: null,
      irregularities_reported_remarks: null,
      information_system_improvement_required: null,
      information_system_improvement_remarks: null,
      reasons_for_non_detection: null,
      non_detection_actions: null,
      ...(incidentDetectionLapseReview.incident_detection_lapse_review ?? {}),
      ...data,
    };

    try {
      await IncidentDetectionLapseReviewAPI.upsert(caseId, {
        incident_detection_lapse_review: fullPayload,
      });
      return fullPayload;
    } catch {
      return rejectWithValue("Failed to update incident detection lapse review");
    }
  },
);
